import { Button } from "@/components/ui/button";
import { CheckCircle2, Shield, Star, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { ContactForm } from "@/components/ContactForm";
import { OurWorkSection } from "@/components/OurWorkSection";
import { SEOHead } from "@/components/SEOHead";

export default function UeberUns() {
  return (
    <>
      <SEOHead
        title="Über uns | Miros Haushaltsauflösung"
        description="Familienbetrieb mit Herz und Struktur. Lernen Sie Miroslav Ozegovic und sein Team für Entrümpelungen in Berlin kennen."
        canonical="https://miros-haushaltsaufloesung.de/ueber-uns"
      />
      <div className="flex flex-col w-full">
        {/* HERO SECTION */}
        <section className="relative w-full pt-32 pb-20 md:pt-40 md:pb-32 flex items-center justify-center bg-[#0c2340] text-white overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c799.jpg"
              alt="Miros Haushaltsauflösung Team"
              width="1200"
              height="800"
              className="w-full h-full object-cover opacity-30"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0c2340] via-[#0c2340]/90 to-transparent"></div>
          </div>

          <div className="container relative z-20 mx-auto px-4 md:px-6">
            <div className="max-w-3xl space-y-8">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.1] tracking-tight">
                Ihr zuverlässiger Partner für Haushaltsauflösungen in Berlin.
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/80 font-medium leading-relaxed">
                Als Berliner Familienbetrieb stehen wir seit 5 Jahren für
                ehrliche Arbeit, Diskretion und transparente Festpreise. Wir
                nehmen Ihnen die Last ab – professionell und einfühlsam.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      className="w-full sm:w-auto h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-lg hover:shadow-xl"
                    >
                      Kostenlose Besichtigung anfragen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                    <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                      <DialogHeader className="mb-5">
                        <DialogTitle className="text-xl sm:text-2xl font-bold">
                          Kostenloses Angebot anfordern
                        </DialogTitle>
                        <DialogDescription className="text-sm sm:text-base">
                          Hinterlassen Sie uns Ihre Details und wir melden uns
                          umgehend bei Ihnen.
                        </DialogDescription>
                      </DialogHeader>
                      <ContactForm />
                    </div>
                  </DialogContent>
                </Dialog>
                <a
                  href="tel:015253181061"
                  className="flex items-center justify-center w-full sm:w-auto h-14 px-8 text-lg font-bold border border-white/20 bg-white/5 text-white hover:bg-white/10 rounded-md shadow-sm transition-colors"
                >
                  <Phone className="mr-2 h-5 w-5" /> 01525 3181061
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* OUR STORY SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 text-primary font-bold tracking-wide uppercase">
                  <Shield className="h-5 w-5" />
                  <span>Über uns</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
                  Familienbetrieb mit Herz und Struktur
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground">
                  <p>
                    Miros Haushaltsauflösung wurde mit einem klaren Ziel
                    gegründet: Menschen in emotional belastenden Situationen
                    eine echte Stütze zu sein. Oft steht eine Haushaltsauflösung
                    nach einem Todesfall, einem Umzug ins Pflegeheim oder einer
                    unerwarteten Veränderung an. Wir wissen, wie schwer diese
                    Momente sind.
                  </p>
                  <p>
                    Deshalb bieten wir nicht nur Manpower, sondern auch Empathie
                    und Struktur. Wir arbeiten diskret, schnell und mit höchster
                    Sorgfalt. Bei uns gibt es keine versteckten Kosten oder böse
                    Überraschungen – ein Wort ist ein Wort.
                  </p>
                  <p>
                    In über 500 erfolgreichen Projekten in Berlin und Umgebung
                    haben wir bewiesen, dass wir auf jede Herausforderung
                    vorbereitet sind. Von der engen Kreuzberger Altbauwohnung
                    bis zur großen Villa in Zehlendorf: Wir machen aus vollen
                    Räumen wieder freie Räume.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute -inset-4 bg-secondary rounded-sm transform rotate-3 z-0"></div>
                <img
                  src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900eb2e956bc24f8c791.jpg"
                  alt="Unser Team bei der Arbeit"
                  width="800"
                  height="600"
                  className="relative z-10 w-full h-auto aspect-[4/3] object-cover rounded-sm shadow-xl"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute -bottom-8 -left-8 z-20 bg-primary text-primary-foreground p-6 rounded-sm shadow-xl max-w-xs hidden md:block">
                  <div className="font-heading font-bold text-4xl mb-1">
                    500+
                  </div>
                  <div className="text-sm font-medium opacity-90">
                    Erfolgreich betreute Projekte
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OWNER SECTION */}
        <section className="py-20 md:py-32 bg-secondary/30">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto bg-background rounded-sm shadow-xl border border-border overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="h-64 md:h-auto relative">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a900e659bec99fc9f3afb.jpg"
                    alt="Miroslav Ozegovic"
                    width="400"
                    height="500"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <p className="text-sm text-muted-foreground uppercase tracking-wider font-bold mb-2">
                    Inhaber & Gründer
                  </p>
                  <h3 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Miroslav Ozegovic
                  </h3>
                  <p className="text-lg text-muted-foreground mb-8 italic">
                    "Für mich ist eine Haushaltsauflösung mehr als nur das
                    Räumen von Möbeln. Es geht darum, unseren Kunden in einer
                    stressigen Zeit eine Last abzunehmen und ihnen das Gefühl zu
                    geben, dass alles in sicheren und professionellen Händen
                    ist."
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="tel:015253181061"
                      className="inline-flex items-center justify-center h-12 px-6 text-base font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-md transition-colors"
                    >
                      <Phone className="mr-2 h-5 w-5" /> Direkt anrufen
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <OurWorkSection />

        {/* VALUES SECTION */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground mb-6">
                Unsere Werte, Ihr Vorteil
              </h2>
              <p className="text-lg text-muted-foreground">
                Wir arbeiten nach klaren Prinzipien, auf die Sie sich verlassen
                können.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "100% Festpreisgarantie",
                  desc: "Wir besichtigen kostenlos und machen Ihnen ein verbindliches Angebot. Keine Nachverhandlungen, keine versteckten Gebühren. Sie wissen von Anfang an, womit Sie kalkulieren können.",
                  icon: <Shield className="h-8 w-8" />,
                },
                {
                  title: "Absolute Diskretion",
                  desc: "Wir arbeiten leise und unauffällig. Ohne große Logos auf der Arbeitskleidung, um Ihre Privatsphäre und die Ihrer Nachbarn zu respektieren. Vertrauliche Unterlagen werden sicher vernichtet.",
                  icon: <CheckCircle2 className="h-8 w-8" />,
                },
                {
                  title: "Umweltbewusstsein",
                  desc: "Wir werfen nicht einfach alles weg. Wir sortieren sorgfältig vor Ort: Verwertbares geht an Recyclingbetriebe oder Second-Hand-Läden, der Rest wird fachgerecht und umweltschonend entsorgt.",
                  icon: <Star className="h-8 w-8" />,
                },
              ].map((value, idx) => (
                <div
                  key={idx}
                  className="bg-secondary/20 p-8 rounded-sm border border-border flex flex-col items-start text-left"
                >
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-sm flex items-center justify-center mb-6 shadow-sm">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 md:py-32 bg-primary">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary-foreground leading-tight">
                Lernen Sie uns persönlich kennen.
              </h2>
              <p className="text-xl text-primary-foreground/80">
                Vereinbaren Sie jetzt einen kostenlosen Besichtigungstermin. Wir
                beraten Sie gerne direkt vor Ort in Berlin und Umgebung.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      className="h-14 px-8 text-lg font-bold bg-accent text-accent-foreground hover:bg-accent/90 rounded-md shadow-xl transition-transform hover:scale-105 w-full sm:w-auto"
                    >
                      Kostenfreie Besichtigung anfragen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="w-[95vw] max-w-[500px] p-0 overflow-hidden bg-background sm:rounded-xl">
                    <div className="p-5 sm:p-8 max-h-[85vh] overflow-y-auto">
                      <DialogHeader className="mb-5">
                        <DialogTitle className="text-xl sm:text-2xl font-bold">
                          Kostenloses Angebot anfordern
                        </DialogTitle>
                        <DialogDescription className="text-sm sm:text-base">
                          Hinterlassen Sie uns Ihre Details und wir melden uns
                          umgehend bei Ihnen.
                        </DialogDescription>
                      </DialogHeader>
                      <ContactForm />
                    </div>
                  </DialogContent>
                </Dialog>
                <a
                  href="tel:015253181061"
                  className="flex items-center justify-center w-full sm:w-auto h-14 px-8 text-lg font-bold border border-white/20 bg-white/5 text-white hover:bg-white/10 rounded-md shadow-sm transition-colors"
                >
                  <Phone className="mr-2 h-5 w-5" /> 01525 3181061
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
